import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    Alert
} from '@mui/material';
import { auth, functions } from '../../api/firebase';
import { useStrings } from '../../common-strings';
import { useUserProfileStrings } from './userProfile-strings';
import * as Sentry from '@sentry/react';
import { isExpectedEmailUpdateCallableError } from '../../api/sentry';

type EmailEditDialogProps = {
    open: boolean;
    onClose: () => void;
    currentEmail: string;
    onEmailUpdated: (newEmail: string) => void;
};

export default function EmailEditDialog({ open, onClose, currentEmail, onEmailUpdated }: EmailEditDialogProps) {
    const profileStrings = useUserProfileStrings();
    const commonStrings = useStrings();
    const [newEmail, setNewEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const effectiveCurrentEmail = (currentEmail || auth.currentUser?.email || '').trim();

    const handleSubmit = async () => {
        if (!newEmail.trim()) {
            setError(profileStrings.pleaseEnterEmail);
            return;
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(newEmail)) {
            setError(profileStrings.pleaseEnterValidEmail);
            return;
        }

        if (newEmail.toLowerCase() === effectiveCurrentEmail.toLowerCase()) {
            setError(profileStrings.emailMustBeDifferent);
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const updateUserEmail = functions.httpsCallable('updateUserEmail');
            const result = await updateUserEmail({ newEmail });

            if (result.data.success) {
                setSuccess(true);
                onEmailUpdated(newEmail);
                
                // Close dialog after a short delay to show success message, then log out
                setTimeout(async () => {
                    onClose();
                    setSuccess(false);
                    setNewEmail('');
                    
                    // Log out the user after email update
                    try {
                        await auth.signOut();
                        // The authentication state change will automatically show the login screen
                        // No need to redirect - the app will handle this naturally
                    } catch (logoutError) {
                        console.error('Error during logout:', logoutError);
                        Sentry.captureException(logoutError);
                    }
                }, 2000); // Increased delay to 2 seconds to show the message
            }
        } catch (error: any) {
            console.error('Error updating email:', error);
            
            let errorMessage = profileStrings.errorUpdatingEmail;
            
            if (error.code === 'functions/already-exists') {
                errorMessage = profileStrings.emailAlreadyInUse;
            } else if (error.code === 'functions/invalid-argument') {
                errorMessage = error.message || profileStrings.invalidEmail;
            } else if (error.code === 'functions/unauthenticated') {
                errorMessage = profileStrings.pleaseLoginAgain;
            } else if (error.code === 'functions/permission-denied') {
                errorMessage = profileStrings.noPermission;
            }
            
            setError(errorMessage);
            if (!isExpectedEmailUpdateCallableError(error)) {
                Sentry.captureException(error);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        if (!isLoading) {
            setError('');
            setNewEmail('');
            onClose();
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                {profileStrings.editEmailTitle}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {`${profileStrings.currentEmailPrefix} ${effectiveCurrentEmail || '—'}`}
                    </Typography>
                    
                    <TextField
                        autoFocus
                        margin="dense"
                        label={profileStrings.newEmailLabel}
                        type="email"
                        fullWidth
                        variant="outlined"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        disabled={isLoading}
                        error={!!error}
                        helperText={error}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !isLoading) {
                                handleSubmit();
                            }
                        }}
                    />
                    
                    {success && (
                        <Alert severity="success" sx={{ mt: 2 }}>
                            {profileStrings.emailUpdatedSuccessfully}
                        </Alert>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button 
                    onClick={handleClose} 
                    disabled={isLoading}
                >
                    {commonStrings.cancel2}
                </Button>
                <Button 
                    onClick={handleSubmit} 
                    variant="contained" 
                    disabled={isLoading || !newEmail.trim()}
                >
                    {isLoading ? profileStrings.updating : profileStrings.update}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
